import { Point } from 'geojson';
import {
    AbbrTracklog,
    AxisIdKey,
    Debrief,
    FlightEvent,
    LngLatAlt,
    Segment,
    SegmentWithLevel,
    SharedTracklog,
    Tracklog,
    TrackPoints,
    TrimmedTracklogValues,
} from 'ff/debrief/models';
import { fromUnixTime, intervalToDuration } from 'date-fns';
import moment from 'moment';
import { LngLatBounds } from '@foreflight/webmap/jmkw';
import { LngLat } from '@foreflight/webmap/core';
import { boundsFromCoordinates } from '@foreflight/webmap/util/geometryBounds';
import { RouteContextType } from 'ff/debrief/router/RouteContext';

export const PAGE_SIZE = 20;
export const ROW_HEIGHT = 85;
export const PAGE_HEIGHT = PAGE_SIZE * ROW_HEIGHT;
export const MAX_FLIGHT_EVENTS = 5;
export const TIME_INTERVAL = 100; // Reduced to 100ms for smoother animation
export const FRAME_RATE = 50;
export const ANALYZE_BUTTON_CACHE_KEY = 'analyze_button';

export const KTS_TO_MPH = 1.15078;

export type Color = 'red' | 'yellow' | 'green';
export type Division = { start: number; end: number; color: string };
export type Divisions = { [color in Color]: Division };

export const SCORE_DIVISIONS: Divisions = {
    red: { start: 0, end: 50, color: '#dd2728' },
    yellow: { start: 50, end: 85, color: '#dab42c' },
    green: { start: 85, end: 100, color: '#1f8029' },
};

export const MAP_BOUNDS = {
    top: 15,
    right: 100,
    bottom: 150,
    left: 310,
};

export const tracklogPointIndex = {
    timestamp: 0,
    latitude: 1,
    longitude: 2,
    groundSpeed: 3,
    altitudeFeet: 4,
    cabinAltitudeFeet: 5,
    bankDeg: 6,
    pitchDeg: 7,
    gLoad: 8,
    trueAirSpeed: 9,
    magneticCourse: 10,
    magneticHeading: 11,
    windDirection: 12,
    windVelocity: 13,
    verticalSpeed: 14,
    turnRate: 15,
    magneticVariance: 16,
    indicatedAirSpeed: 17,
    crosswind: 18,
    headwind: 19,
    aboveGroundLevel: 20,
    terrainElevation: 21,
    distanceFromStart: 22,
    distanceToRunway: 23,
} as const;

export const axisId: AxisIdKey = {
    AltitudeAxis: 'altitude',
    AboveGroundLevelAxis: 'above_ground_level',
    AirspeedAxis: 'airspeed',
    GroundSpeedAxis: 'ground_speed',
    BankAxis: 'bank',
    PitchAxis: 'pitch',
    GLoadAxis: 'g_load',
    TerrainElevationAxis: 'terrain_elevation',
} as const;

export function getBoundedScore(score: number): number {
    let boundedScore: number;
    if (!(typeof score === 'number')) boundedScore = null;
    else if (score < 0) boundedScore = 0;
    else if (score > 100) boundedScore = 100;
    else boundedScore = Math.round(score);

    return boundedScore;
}

export function getScoreColor(score: number): string {
    const { red, yellow, green } = SCORE_DIVISIONS;
    const boundedScore = getBoundedScore(score);
    if (boundedScore === null) return null;

    let scoreColor: string;
    if (boundedScore < red.end) scoreColor = red.color;
    else if (boundedScore < yellow.end) scoreColor = yellow.color;
    else scoreColor = green.color;

    return scoreColor;
}

export function getScoreRange(color: Color): [start: number, end: number] {
    switch (color) {
        case 'red':
            return [SCORE_DIVISIONS.red.start, SCORE_DIVISIONS.red.end];
        case 'yellow':
            return [SCORE_DIVISIONS.yellow.start, SCORE_DIVISIONS.yellow.end];
        case 'green':
            return [SCORE_DIVISIONS.green.start, SCORE_DIVISIONS.green.end];
        default:
            return undefined;
    }
}

export function pointsToCoordinates(points: TrackPoints): LngLatAlt[] {
    return points.data.map(p => [p[2], p[1], p[4]]);
}

export function pointToCoordinate(point: number[]): LngLat {
    return [point[2], point[1]];
}

export function durationSecondsToString(durationSeconds: number) {
    if (!durationSeconds) {
        return '';
    }
    const duration = intervalToDuration({
        start: 0,
        end: durationSeconds * 1000,
    });
    return `${duration.hours > 0 ? duration.hours + 'h' : ''}${
        duration.minutes || 0
    }m${
        (!duration.hours || duration.hours <= 0) && duration.seconds
            ? duration.seconds + 's'
            : (!duration.hours || duration.hours <= 0) && !duration.seconds
              ? '00s'
              : ''
    }`;
}

export function formatDuration(durationSeconds: number): string {
    if (durationSeconds === 0) {
        return '00:00';
    }
    if (!durationSeconds) {
        return '';
    }
    const duration = intervalToDuration({
        start: 0,
        end: durationSeconds * 1000,
    });
    const hours = duration.hours ? duration.hours.toString() : null;
    const minutes = duration.minutes.toString().padStart(2, '0');
    const seconds = duration.seconds.toString().padStart(2, '0');

    return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
}

export const constructFlightEvents = (
    tracklog?: Tracklog,
    debriefData?: Debrief
) => {
    const metadata = tracklog?.metadata;
    const flightEvents: FlightEvent[] = [];

    if (debriefData?.instrumentProcedureCount > 0) {
        flightEvents.push({
            eventType: 'iap',
            userText: 'Inst Appr',
            count: debriefData?.instrumentProcedureCount,
        });
    }

    if (debriefData?.landings > 0) {
        flightEvents.push({
            eventType: 'landings',
            userText: 'Landings',
            count: debriefData?.landings,
        });
    } else if (tracklog?.eventDetails.landings > 0) {
        flightEvents.push({
            eventType: 'landings',
            userText: 'Landings',
            count: tracklog.eventDetails.landings,
        });
    }

    if (metadata?.initialGPSSource?.toLowerCase().includes('sentry')) {
        flightEvents.push({
            eventType: 'sentry',
            userText: 'Sentry',
        });
    }

    return flightEvents.slice(0, MAX_FLIGHT_EVENTS) || [];
};

export const extractTrimmedValues = (
    tracklog: Tracklog | SharedTracklog
): TrimmedTracklogValues => {
    if (!tracklog) return null;
    const metaData = tracklog?.metadata;

    let trimmedDurationSeconds;
    if (metaData?.trimStart && metaData?.trimEnd) {
        trimmedDurationSeconds = metaData.trimEnd - metaData.trimStart;
    }
    return {
        timestampStart: metaData?.trimStart || metaData?.timestampStart,
        timestampEnd: metaData?.trimEnd || metaData?.timestampEnd,
        totalDurationSeconds:
            trimmedDurationSeconds || tracklog?.totalDurationSeconds,
        actualFlightTime:
            metaData?.trimmedActualFlightTime || metaData?.actualFlightTime,
        avgGndspd:
            metaData?.trimmedAvgGndspd ||
            metaData?.avgGndspd ||
            tracklog?.avgGndspd,
        distTotal: metaData?.trimmedDistTotal || metaData?.distTotal,
        latEnd: metaData?.trimmedLatEnd || metaData?.latEnd,
        latStart: metaData?.trimmedLatStart || metaData?.latStart,
        lonEnd: metaData?.trimmedLonEnd || metaData?.lonEnd,
        lonStart: metaData?.trimmedLonStart || metaData?.lonStart,
        maximumG: metaData?.trimmedMaximumG || metaData?.maximumG,
        minimumG: metaData?.trimmedMinimumG || metaData?.minimumG,
    };
};

export function formatSpeed(speed: number, unit?: string): string {
    if (!speed) return '';
    const displayUnit = unit || 'kts';
    return `${convertSpeed(speed, displayUnit)} ${displayUnit}`;
}

export function formatDistance(distance: number): string {
    if (!distance) return '';
    return `${Math.ceil(distance)} nm`;
}

export function formatWeight(rawWeight: number): string {
    if (typeof rawWeight !== 'number') return '';
    return (rawWeight * 100).toFixed(0) + '%';
}

export function formatDate(timestamp: number): string {
    if (!timestamp) return '';
    return fromUnixTime(timestamp).toLocaleString(undefined, {
        hour12: true,
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
    });
}

export function formatTime(timestamp: number, withSeconds?: boolean): string {
    if (!timestamp) return '';
    const format: Intl.DateTimeFormatOptions = {
        hour12: true,
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
    };
    if (withSeconds) {
        format.second = 'numeric';
    }
    return fromUnixTime(timestamp).toLocaleString(undefined, format);
}

/**
 * Given a list of points, finds the nearest point to a given timestamp via a binary search.
 * The list of points must be sorted by timestamp in ascending order.
 *
 * @param {number[][]} points - The list of points to search.
 * @param {number} timestamp - The timestamp to find the nearest point to.
 * @returns {number[]} - The index of the nearest point to the given timestamp.
 */
export function findNearestPoint(
    points: number[][],
    timestamp: number
): number {
    if (!points || points.length === 0 || !timestamp) return null;
    let left = 0;
    let right = points.length - 1;

    // Binary search down to two adjacent points
    while (Math.abs(right - left) > 1) {
        const mid = Math.floor((left + right) / 2);
        const point = points[mid];
        const pointTimestamp = point[0];

        if (pointTimestamp === timestamp) {
            return mid;
        } else if (pointTimestamp < timestamp) {
            left = mid;
        } else {
            right = mid;
        }
    }

    // Narrowed down to two adjacent points. Now return nearest point
    const leftPoint = points[left];
    const rightPoint = points[right];
    const leftTimestamp = leftPoint[0];
    const rightTimestamp = rightPoint[0];

    if (
        Math.abs(leftTimestamp - timestamp) <
        Math.abs(rightTimestamp - timestamp)
    ) {
        return left;
    } else {
        return right;
    }
}

export function createGeojsonPoint(latitude: number, longitude: number): Point {
    if (!latitude || !longitude) return null;
    return {
        type: 'Point',
        coordinates: [longitude, latitude],
    };
}

export function trackPointToGeojsonPoint(trackPoint: number[]): Point {
    if (!trackPoint) return null;
    return createGeojsonPoint(trackPoint[1], trackPoint[2]);
}

export function flattenDebriefSegments(
    segments: Segment[],
    level: number = 0
): SegmentWithLevel[] {
    const flat: SegmentWithLevel[] = [];

    if (!segments || segments.length === 0) return [];

    segments.forEach(segment => {
        if (segment.children && segment.label !== 'airborne') {
            flat.push({ ...segment, level });
            flat.push(...flattenDebriefSegments(segment.children, level + 1));
        } else {
            flat.push({ ...segment, level });
        }
    });

    return flat;
}

export const isCurrentSegment = (currentOffset: number, segment: Segment) =>
    currentOffset >= segment.startOffsetInSeconds &&
    currentOffset < segment.startOffsetInSeconds + segment.durationInSeconds;

/**
 * Given an array of points in the following format:
 *     [timestamp, ...otherValues]
 * this will return the points within the "start" and "end" timestamps
 * inclusively.
 * @param points
 * @param start
 * @param end
 */
export function pointsInRange(
    points: number[][],
    start: number,
    end: number
): number[][] {
    const pointsInRange: number[][] = [];
    points.forEach(point => {
        if (point[0] >= start && point[0] <= end) {
            pointsInRange.push(point);
        }
    });
    return pointsInRange;
}

export function buildShareUrl(shareUuid: string): string {
    return `${window.location.origin}/shared/debrief/track/${shareUuid}`;
}

/**
 * Given a tree of segments, will return the segment most specific to the
 * provided offset.
 * @param segments tree of segments
 * @param offset
 */
export const findCurrentSegment = (segments: Segment[], offset: number) => {
    const foundSegments: Segment[] = [];
    // flattenDebriefSegments create a list that is an in-order traversal of the segment tree
    const segmentList = flattenDebriefSegments(segments);
    for (const segment of segmentList) {
        if (isCurrentSegment(offset, segment)) {
            foundSegments.push(segment);
        }
    }
    return foundSegments.pop();
};

export const findSegmentByLabelAndOffset = (
    segments: Segment[],
    label: string,
    targetOffset: number
): Segment | undefined => {
    const TOLERANCE_SECONDS = 20; // Buffer since offsets can change upon reanalysis
    const targetLabel = label.trim().toLowerCase();

    const segmentList = flattenDebriefSegments(segments);
    const labelMatches = segmentList.filter(
        segment => segment.label?.trim().toLowerCase() === targetLabel
    );

    const matchByLabelAndOffset = labelMatches.find(match => {
        const matchStart = match.startOffsetInSeconds;
        const matchEnd = matchStart + match.durationInSeconds;

        return (
            targetOffset >= matchStart - TOLERANCE_SECONDS &&
            targetOffset < matchEnd + TOLERANCE_SECONDS
        );
    });

    if (matchByLabelAndOffset) return matchByLabelAndOffset;

    // If current label changes, search by offset and first word match
    const matchByOffset = findCurrentSegmentRecursive(segments, targetOffset);

    if (matchByOffset) {
        const originalFirstWord = label?.trim().split(' ')[0]?.toLowerCase();
        const matchFirstWord = matchByOffset?.label
            ?.trim()
            .split(' ')[0]
            ?.toLowerCase();

        if (originalFirstWord === matchFirstWord) {
            return matchByOffset;
        }
    }

    return segments[0];
};

/**
 * Recursively searches down from the top level segment to find the deepest
 * descendant that satisfies isCurrentSegment. If the first child has the
 * exact same timestamp as the parent, then the parent segment is returned.
 * @param segment
 * @param offset
 */
function findSegmentRecursiveHelper(
    segment: Segment,
    offset: number
): Segment | null {
    if (!isCurrentSegment(offset, segment)) {
        return null;
    }

    let childMatch: Segment = null;
    for (const child of segment.children) {
        const match = findSegmentRecursiveHelper(child, offset);
        if (match) {
            childMatch = match;
            break;
        }
    }

    if (childMatch) {
        const firstChild = segment.children[0];

        if (
            firstChild?.id === childMatch.id &&
            firstChild?.startUtc === childMatch.startUtc
        ) {
            return segment;
        } else {
            return childMatch;
        }
    }

    return segment;
}

export const findCurrentSegmentRecursive = (
    segments: Segment[],
    offset: number
): Segment => {
    for (const segment of segments) {
        const match = findSegmentRecursiveHelper(segment, offset);
        if (match) {
            return match;
        }
    }
    return null;
};

// COMMON UTILS: ported over from old common directory
export function getCookieItem(sKey: string) {
    if (!sKey) {
        return null;
    }
    const value = decodeURIComponent(
        document.cookie.replace(
            new RegExp(
                '(?:(?:^|.*;)\\s*' +
                    // eslint-disable-next-line no-useless-escape
                    encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') +
                    '\\s*\\=\\s*([^;]*).*$)|^.*$'
            ),
            '$1'
        )
    );
    return !value || value === 'null' ? null : value;
}

/**
 * Takes two arrays of track logs and finds the tracks that have been updated
 * with a different analysis summary status. Will only check if any tracks
 * from the previous list have been updated.
 * @param prevTracks
 * @param newTracks
 * @return list of tracks that have been updated from prevTracks
 */
export function findUpdatedTracks(
    prevTracks: AbbrTracklog[],
    newTracks: AbbrTracklog[]
): AbbrTracklog[] {
    const updatedTracks: AbbrTracklog[] = [];
    for (const prevTrack of prevTracks) {
        const newTrack = newTracks.find(
            t => t.trackUuid === prevTrack.trackUuid
        );
        if (
            newTrack &&
            newTrack?.analysisSummary?.status !==
                prevTrack?.analysisSummary?.status
        ) {
            updatedTracks.push(newTrack);
        }
    }
    return updatedTracks;
}

export function boundsFromPoints(points: number[][]): LngLatBounds {
    return boundsFromCoordinates(points.map(p => [p[2], p[1]]));
}

/**
 * Check if the user has a license containing debrief. Can eventually be edited
 * if the debrief subscription tier changes.
 *
 * @return {boolean} True if the user has a Premium license, false otherwise.
 */
export function hasDebriefLicense(): boolean {
    const user = window.currentUser;
    if (!user) {
        return false;
    }

    return user.hasDebrief;
}

/**
 * Given a flightDateString, formats into Month Day, Year (ie Sep 5, 2028)
 * @param date
 */
export function formatFlightDate(date?: string): string {
    if (!date) return 'N/A';
    const localDate = new Date(date + 'T00:00:00');

    return localDate.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

/**
 * Determines the current page based on the pathname for analytics tracking
 * @param pathname
 * @returns The current page name
 */
export function getCurrentPage(pathname: string): string {
    if (pathname.includes('/debrief/track/')) return 'Shared Debrief';
    if (pathname.includes('/shared/')) return 'Shared Tracklog';
    if (pathname.endsWith('/debrief')) return 'Debrief';
    if (pathname.match(/\/[^/]+$/)) return 'Tracklog Detail';
    return 'Tracklog List';
}

/**
 * Calculates the crosswind speed from wind direction, heading and wind velocity.
 * A positive value indicates a crosswind from the right, and a negative value
 * indicates a crosswind from the left.
 * @param windDirection
 * @param heading
 * @param windVelocity
 * @returns The crosswind speed
 */
export function calculateCrosswindSpeed(
    windDirection: number,
    heading: number,
    windVelocity: number
): number {
    if (windDirection == null || heading == null || windVelocity == null) {
        return null;
    }

    const windAngleDegrees = windDirection - heading;
    const windAngleRadians = windAngleDegrees * (Math.PI / 180);
    return windVelocity * Math.sin(windAngleRadians);
}

/**
 * Formats a number into a display read string for the instrument panel.
 * Returns '--' for null values, rounds the number to a specified number of
 * decimal places, and adds thousands seperators. Optionally adds leading 0's
 * if specified.
 * @param num The number to be formatted.
 * @param decimals The number of decimal places to round to, defaults to 0.
 * @param pad A boolean that if true, will pad the final string with leading 0's.
 * @returns A formatted number as a string.
 */
export function formatValue(num: number | null, decimals = 0, pad = false) {
    if (num == null) return '--';
    const roundedValue =
        decimals > 0 ? parseFloat(num.toFixed(decimals)) : Math.round(num);
    const formattedValue = (
        Object.is(roundedValue, -0) ? 0 : roundedValue
    ).toLocaleString();
    return pad ? formattedValue.padStart(3, '0') : formattedValue;
}

/**
 * Checks if a page is shared or not
 * @param page The page context
 * @returns If the page is a share page.
 */
export const isSharedPage = (page: RouteContextType['page']): boolean => {
    const SHARED_PAGES: Array<RouteContextType['page']> = [
        'SharedWithMeView',
        'SharedDebriefView',
        'AnonSharedView',
    ];
    return page && SHARED_PAGES.includes(page);
};

/**
 * Infers the unit type from label and goal strings. Important: This is
 * a work around because the server does not provide the unit type.
 * @param label The label text that might contain unit information
 * @param goal The goal text that might contain unit information
 * @returns The inferred unit string (e.g., ' kts', '°', ' fpm')
 */
export function inferUnit(label: string, goal: string): string {
    const combinedText = `${label || ''} ${goal || ''}`.toLowerCase();

    if (combinedText.includes('kts') || combinedText.includes('knots'))
        return ' kts';
    if (
        combinedText.includes('fpm') ||
        combinedText.includes('feet per minute')
    )
        return ' fpm';
    if (combinedText.includes('deg') || combinedText.includes('degree'))
        return '°';
    if (combinedText.includes('dots')) return ' dots';
    if (combinedText.includes('ft') || combinedText.includes('feet'))
        return ' ft';
    if (combinedText.includes('nm') || combinedText.includes('nautical'))
        return ' nm';
    if (combinedText.includes('mph')) return ' mph';
    if (combinedText.includes('m/s')) return ' m/s';

    return '';
}

/**
 * Gets the number of decimal places to use based on value and unit
 * @param value The numeric value
 * @param unit The unit string (from inferUnit)
 * @returns The number of decimal places (null for integer rounding)
 */
function getPrecision(value: number, unit: string): number | null {
    if (Math.abs(value) < 1 || unit === ' nm') {
        return 2;
    } else if (unit === '°') {
        return 1;
    }
    return null;
}

/**
 * Rounds a value to the appropriate precision based on its magnitude and unit type
 * @param value The numeric value to round
 * @param unit The unit string (from inferUnit)
 * @returns The rounded value
 */
export function roundToPrecision(value: number, unit: string): number {
    const precision = getPrecision(value, unit);
    if (precision !== null) {
        return parseFloat(value.toFixed(precision));
    }
    return Math.round(value);
}

/**
 * Formats a value with appropriate precision and unit, preserving trailing zeros
 * @param value The numeric value to format
 * @param unit The unit string (from inferUnit)
 * @returns The formatted string with unit
 */
export function formatValueWithUnit(value: number, unit: string): string {
    const precision = getPrecision(value, unit);
    if (precision !== null) {
        return `${value.toFixed(precision)}${unit}`;
    }
    return `${Math.round(value)}${unit}`;
}

/**
 * Normalizes degree ranges that wrap around 360. If max < min (wrapped range),
 * adds 360 to the max value to make it linear for calculations.
 * @param min The minimum value of the range
 * @param max The maximum value of the range
 * @param unit The unit type (must be '°' for normalization to occur)
 * @returns Normalized min and max values, plus flag indicating if range was wrapped
 */
export function normalizeDegreeRange(
    min: number | null,
    max: number | null,
    unit: string
): {
    normalizedMin: number | null;
    normalizedMax: number | null;
    isWrapped: boolean;
} {
    if (min === null || max === null) {
        return { normalizedMin: min, normalizedMax: max, isWrapped: false };
    }

    const isWrapped = unit === '°' && max < min;

    return {
        normalizedMin: min,
        normalizedMax: isWrapped ? max + 360 : max,
        isWrapped,
    };
}

/**
 * Normalizes an actual value to match a potentially wrapped goal range.
 * If the range is wrapped and the value is less than a threshold, adds 360.
 * Uses 180 as the threshold to determine which side of the wrap the value is on.
 * Ex: Goal = (355 -> 5, wrapped=true). Actual = (1 to 8). Given that it's wrapped,
 * we know we should add 360 since the range is on the wrapped end.
 * @param value The actual value to normalize
 * @param goalMin The minimum of the goal range
 * @param isWrapped Whether the goal range wraps around 360
 * @returns Normalized value
 */
export function normalizeActualDegreeValue(
    value: number,
    goalMin: number | null,
    isWrapped: boolean
): number {
    if (!isWrapped || goalMin === null || value === undefined) {
        return value;
    }

    const WRAP_THRESHOLD = 180;
    return value < WRAP_THRESHOLD ? value + 360 : value;
}

/**
 * Calculates the most significant deviation from goal range.
 * Handles wrapped degree ranges (e.g., 355° to 5°) by normalizing values.
 * @param scoreDetails Object containing actual and goal min/max values, plus label and goal for unit detection
 * @returns Object with deviation value and type (above/below), or null if within range
 */
export function calculateDeviation(scoreDetails: {
    actualMin: number;
    actualMax: number;
    goalMin: number | null;
    goalMax: number | null;
    label?: string;
    goal?: string;
}): { value: number; type: 'above' | 'below' } | null {
    const { actualMin, actualMax, goalMin, goalMax, label, goal } =
        scoreDetails;

    // Detect and normalize wrapped degree ranges
    const unit = inferUnit(label || '', goal || '');
    const goalNormalized = normalizeDegreeRange(goalMin, goalMax, unit);

    const normalizedActualMin = normalizeActualDegreeValue(
        actualMin,
        goalMin,
        goalNormalized.isWrapped
    );
    const normalizedActualMax = normalizeActualDegreeValue(
        actualMax,
        goalMin,
        goalNormalized.isWrapped
    );
    const normalizedGoalMin = goalNormalized.normalizedMin;
    const normalizedGoalMax = goalNormalized.normalizedMax;

    const deviations: { value: number; type: 'above' | 'below' }[] = [];

    if (normalizedGoalMax !== null) {
        if (normalizedActualMax > normalizedGoalMax) {
            deviations.push({
                value: normalizedActualMax - normalizedGoalMax,
                type: 'above',
            });
        }
        if (normalizedActualMin > normalizedGoalMax) {
            deviations.push({
                value: normalizedActualMin - normalizedGoalMax,
                type: 'above',
            });
        }
    }

    if (normalizedGoalMin !== null) {
        if (normalizedActualMin < normalizedGoalMin) {
            deviations.push({
                value: normalizedGoalMin - normalizedActualMin,
                type: 'below',
            });
        }
        if (normalizedActualMax < normalizedGoalMin) {
            deviations.push({
                value: normalizedGoalMin - normalizedActualMax,
                type: 'below',
            });
        }
    }

    if (deviations.length > 0) {
        return deviations.reduce((max, current) =>
            current.value > max.value ? current : max
        );
    }

    return null;
}

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const isCustom = (model: string): boolean => {
    return model?.toLocaleUpperCase() === 'CUSTOM';
};

/**
 * Formats a date range string from Unix timestamps using moment.
 * @param start - The start of the range in Unix seconds.
 * @param end - The end of the range in Unix seconds.
 * @returns A formatted string like "Oct 1, 2025 - Oct 5, 2025".
 */
export const formatDateRange = (start: number, end: number): string => {
    const dateFormat = 'MMM D, YYYY';
    const startDate = start > 0 ? moment.unix(start).format(dateFormat) : null;
    const endDate = end > 0 ? moment.unix(end).format(dateFormat) : null;

    if (startDate && endDate) return `${startDate} - ${endDate}`;
    if (startDate) return `From ${startDate}`;
    if (endDate) return `Until ${endDate}`;

    return '';
};

/**
 * Calculates the speed value to mph or kts based on user input.
 * @param speed Speed in kts (default)
 * @param unit Unit provided, either 'kts' or 'mph'
 * @returns Speed in mph or kts, rounded to the nearest integer
 */
export function convertSpeed(speed: number, unit: string): number {
    if (unit === 'mph') {
        speed *= KTS_TO_MPH;
    }
    return Math.round(speed);
}

/**
 * Converts speed values in score details based on the user's preferred unit.
 * If the inferred unit is kts and the preferred unit is mph, converts the values.
 * @param value The value to potentially convert
 * @param label The label text that might contain unit information
 * @param goal The goal text that might contain unit information
 * @param speedUnit The user's preferred speed unit ('kts' or 'mph')
 * @returns The converted value and the display unit
 */
export function getUnitForScoring(
    value: number,
    label: string,
    goal: string,
    speedUnit?: string
): { value: number; unit: string } {
    const inferredUnit = inferUnit(label, goal);

    // Check if this is a speed measurement in kts
    if (inferredUnit === ' kts' && speedUnit === 'mph') {
        return {
            value: convertSpeed(value, 'mph'),
            unit: ' mph',
        };
    }

    return { value, unit: inferredUnit };
}
